import React from 'react';
import { IconInfoCircle, IconTag, IconTrash } from '@tabler/icons-react';
import { Button } from '@planne-software/uni/Button';
import { Tooltip } from '@planne-software/uni';

import { useTranslate } from '@src/core/hooks';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import { track } from '@src/services/analytics';

import { useCartCoupon } from '../../hooks/useCartCoupon';
import {
  MobileSummaryCouponCode,
  MobileSummaryCouponInfo,
  MobileSummaryCouponRow,
  MobileSummaryRow,
  MobileSummaryRowLabel,
  MobileSummaryRowMutedValue,
  MobileSummaryRowValue,
  MobileSummaryServiceFeeLabel,
  SummarySection,
  SummaryTotalLabel,
  SummaryTotalRow,
  SummaryTotalValue,
} from '../styles';

export type SummaryTotalsViewModel = {
  subtotalCents: number;
  totalAmountCents: number;
  discountCents: number;
  couponCode?: string;
  couponDiscountCents: number;
  couponPercentLabel?: string;
  showServiceFee: boolean;
  serviceFeeAmountCents: number;
  serviceFeeDescription?: string;
  showSubtotal?: boolean;
  showTotal?: boolean;
  canManageCoupon?: boolean;
};

type CartSummaryTotalProps = {
  summary: SummaryTotalsViewModel;
};

export const CartSummaryTotal = (props: CartSummaryTotalProps) => {
  const { summary } = props;
  const { showSubtotal = true, showTotal = true } = summary;
  const { i18n } = useTranslate();
  const { formatCents } = useCurrencyFormat();
  const { remove } = useCartCoupon();

  const handleRemoveCoupon = () => {
    if (!summary.couponCode) return;

    track('cartCouponRemoved', { couponCode: summary.couponCode });
    remove();
  };

  return (
    <SummarySection data-testid='cart-summary-total'>
      {showSubtotal ? (
        <MobileSummaryRow>
          <MobileSummaryRowLabel>{i18n('cart_subtotal')}</MobileSummaryRowLabel>
          <MobileSummaryRowValue>{formatCents(summary.subtotalCents)}</MobileSummaryRowValue>
        </MobileSummaryRow>
      ) : null}

      {summary.discountCents > 0 ? (
        <MobileSummaryRow>
          <MobileSummaryRowLabel>{i18n('checkout_discounts')}</MobileSummaryRowLabel>
          <MobileSummaryRowMutedValue>{`-${formatCents(summary.discountCents)}`}</MobileSummaryRowMutedValue>
        </MobileSummaryRow>
      ) : null}

      {summary.couponCode ? (
        <MobileSummaryCouponRow>
          <MobileSummaryCouponInfo>
            <MobileSummaryCouponCode>
              <IconTag size={20} />
              <span>{summary.couponCode}</span>
            </MobileSummaryCouponCode>
            {summary.canManageCoupon ? (
              <Button
                type='button'
                variant='text'
                size='sm'
                color='neutral'
                startSlot={<IconTrash />}
                onClick={handleRemoveCoupon}
              >
                {i18n('cart_remove_discount_button')}
              </Button>
            ) : null}
          </MobileSummaryCouponInfo>
          <MobileSummaryRowValue>
            {summary.couponPercentLabel ? `${summary.couponPercentLabel} ` : ''}
            {`-${formatCents(summary.couponDiscountCents)}`}
          </MobileSummaryRowValue>
        </MobileSummaryCouponRow>
      ) : null}

      {summary.showServiceFee ? (
        <MobileSummaryRow>
          <MobileSummaryServiceFeeLabel>
            <span>{i18n('cart_serviceFee')}</span>
            <Tooltip arrow placement='top' label={summary.serviceFeeDescription ?? i18n('cart_serviceFee_tooltip')}>
              <IconInfoCircle size={20} />
            </Tooltip>
          </MobileSummaryServiceFeeLabel>
          <MobileSummaryRowValue>{formatCents(summary.serviceFeeAmountCents)}</MobileSummaryRowValue>
        </MobileSummaryRow>
      ) : null}

      {showTotal ? (
        <SummaryTotalRow>
          <SummaryTotalLabel>{i18n('cart_totalLabel')}</SummaryTotalLabel>
          <SummaryTotalValue>{formatCents(summary.totalAmountCents)}</SummaryTotalValue>
        </SummaryTotalRow>
      ) : null}
    </SummarySection>
  );
};

export default CartSummaryTotal;
